"use client";

import posthog from "posthog-js";
import { v4 as uuidv4 } from "uuid";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AuthFlowContext } from "./auth-flow-context";
import { recordRejectedPassword } from "./patient-sign-in-telemetry";
import { AuthAttemptHandle, AuthAttemptOutcome, AuthAttemptStage, AuthFlowActions, AuthFlowContextValue, AuthFlowMeta, AuthFlowState, AuthMethodId, AuthSuccess, AuthSurface } from "./types";
import { isCredentialError, normalizeAuthError } from "./use-auth-error";

// Provider surfaces are staff-facing — never count them as patient logins.
const NON_PATIENT_SURFACES: ReadonlySet<AuthSurface> = new Set<AuthSurface>([AuthSurface.PROVIDER_LOGIN, AuthSurface.INVITE]);
function captureAttemptEvent(name: string, fields: Record<string, unknown>): void {
  try {
    posthog.capture(name, fields);
  } catch {
    // Telemetry is observational; a blocked SDK must not block sign-in.
  }
}
function classifyAttemptError(error: unknown): AuthAttemptOutcome {
  const code = (error as {
    code?: unknown;
  } | null)?.code;
  const status = (error as {
    response?: {
      status?: number;
    };
  } | null)?.response?.status;
  if (isCredentialError(error) || code === "auth/invalid-verification-code" || code === "auth/invalid-verification-id") {
    return "credential_rejected";
  }
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") return "cancelled";
  if (code === "auth/network-request-failed" || error instanceof TypeError || (error as Error | null)?.name === "TimeoutError") {
    return "network_error";
  }
  if (typeof code === "string" && code.startsWith("auth/")) return "firebase_error";
  if (status === 403) return "backend_access_denied";
  if (status === 408) return "network_error";
  if (typeof status === "number") return "backend_error";
  // The legacy Google popup returns a friendly string without its Firebase
  // code, so an ordinary Error cannot reliably be attributed to the backend.
  return "unclassified_error";
}
export interface AuthFlowProps {
  surface: AuthSurface;
  defaultMethod: AuthMethodId;
  enabledMethods?: ReadonlyArray<AuthMethodId>;
  onSuccess: (credential: AuthSuccess) => Promise<void> | void;
  /** See AuthFlowMeta.allowPortalAccountHolders. */
  allowPortalAccountHolders?: boolean;
  children: ReactNode;
}
export function AuthFlow({
  surface,
  defaultMethod,
  enabledMethods,
  onSuccess,
  allowPortalAccountHolders,
  children
}: AuthFlowProps) {
  const [activeMethod, setActiveMethod] = useState<AuthMethodId>(defaultMethod);
  const [runningMethod, setRunningMethod] = useState<AuthMethodId | null>(null);
  const [errors, setErrors] = useState<AuthFlowState["errors"]>({});
  const [isInSubFlow, setInSubFlow] = useState<boolean>(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  useEffect(() => {
    // Sample every auth flow — sign-in/sign-up pages, plus any feature
    // (forms, self-schedule, billing, …) that gates on AuthFlow — so we
    // can debug auth issues while base recording stays enabled globally.
    posthog.startSessionRecording({
      sampling: true,
      linked_flag: true,
      url_trigger: true,
      event_trigger: true
    });
  }, []);
  const clearError = useCallback((method: AuthMethodId) => {
    setErrors(prev => {
      if (!(method in prev)) return prev;
      const {
        [method]: _removed,
        ...rest
      } = prev;
      return rest;
    });
  }, []);
  const setError = useCallback((method_0: AuthMethodId, message: string) => {
    setErrors(prev_0 => ({
      ...prev_0,
      [method_0]: message
    }));
  }, []);

  // Stable signature so callers passing an inline `enabledMethods` array
  // don't churn `meta` (and every downstream effect keyed on it) every
  // render. Sorted because membership is the only thing consumers care
  // about — order is meaningless here.
  const enabledMethodsKey = useMemo(() => [...(enabledMethods ?? [defaultMethod])].sort().join(","), [enabledMethods, defaultMethod]);
  const offersGoogle = enabledMethodsKey.split(",").includes(AuthMethodId.GOOGLE);
  const startAttempt = useCallback((method_1: AuthMethodId, stage: AuthAttemptStage, isResend = false): AuthAttemptHandle | null => {
    if (NON_PATIENT_SURFACES.has(surface) || surface === AuthSurface.PATIENT_SIGN_UP) return null;
    const attemptId = uuidv4();
    const fields = {
      attempt_id: attemptId,
      method: method_1,
      stage,
      surface,
      is_resend: isResend
    };
    captureAttemptEvent("patient_sign_in_attempt_started", fields);
    let finished = false;
    return {
      finish(outcome) {
        if (finished) return;
        finished = true;
        captureAttemptEvent("patient_sign_in_attempt_outcome", {
          ...fields,
          outcome
        });
      },
      fail(error) {
        this.finish(classifyAttemptError(error));
      }
    };
  }, [surface]);
  const runAuth = useCallback(async <T,>(method_2: AuthMethodId, fn: () => Promise<T>, attempt?: AuthAttemptHandle | null): Promise<T | null> => {
    setRunningMethod(method_2);
    setErrors(prev_1 => {
      if (!(method_2 in prev_1)) return prev_1;
      const {
        [method_2]: _removed,
        ...rest_0
      } = prev_1;
      return rest_0;
    });
    try {
      const result = await fn();
      return result;
    } catch (err) {
      attempt?.fail(err);
      if (method_2 === AuthMethodId.PASSWORD && !NON_PATIENT_SURFACES.has(surface) && surface !== AuthSurface.PATIENT_SIGN_UP && isCredentialError(err)) {
        recordRejectedPassword(surface, offersGoogle);
      }
      setErrors(prev_2 => ({
        ...prev_2,
        [method_2]: normalizeAuthError(err, {
          offersGoogle
        })
      }));
      return null;
    } finally {
      setRunningMethod(null);
    }
  }, [offersGoogle, surface]);
  const actions = useMemo<AuthFlowActions>(() => ({
    setActiveMethod,
    runAuth,
    startAttempt,
    clearError,
    setError,
    setInSubFlow
  }), [runAuth, startAttempt, clearError, setError]);
  const meta = useMemo<AuthFlowMeta>(() => ({
    surface,
    enabledMethods: new Set<AuthMethodId>(enabledMethodsKey ? enabledMethodsKey.split(",") as AuthMethodId[] : []),
    onSuccess: (credential, attempt_0) => {
      // Credential acceptance is not yet a completed tenant login. The
      // resolver records success only after resolving an allowed patient.
      try {
        const result_0 = onSuccessRef.current(credential);
        if (attempt_0) {
          void Promise.resolve(result_0).then(() => attempt_0.finish("access_unconfirmed"), (error_1: unknown) => attempt_0.fail(error_1));
        }
        return result_0;
      } catch (error_0) {
        attempt_0?.fail(error_0);
        throw error_0;
      }
    },
    allowPortalAccountHolders
  }), [surface, enabledMethodsKey, allowPortalAccountHolders]);
  const value = useMemo<AuthFlowContextValue>(() => ({
    state: {
      activeMethod,
      runningMethod,
      errors,
      isInSubFlow
    },
    actions,
    meta
  }), [activeMethod, runningMethod, errors, isInSubFlow, actions, meta]);
  return <AuthFlowContext.Provider value={value}>
      {children}
    </AuthFlowContext.Provider>;
}